import {
  PropertyDefinitionType,
  Resolution
} from '@/templates/models/templates';
import { create } from 'zustand';
import { EditorImagePropertyData, EditorProperty } from '../models/editor';
import { ParsedUrlQuery } from 'querystring';

interface GraphicData {
  resources: Record<string, EditorImagePropertyData>;
  properties: {
    values: Record<string, string | Record<string, string>[]>;
    definitions: PropertyDefinitionType[][];
  };
  hasEditorErrors: boolean;
}

interface Metadata {
  template: {
    id: string;
    moduleId: string;
  };
  graphic: {
    id: string;
    name: string;
  };
}
export interface EditorQueryParams extends ParsedUrlQuery {
  templateId?: string;
  graphicId?: string;
  resolution?: string;
}

export type LayoutsById = {
  content: PropertyDefinitionType[][];
  media: PropertyDefinitionType[][];
  customization: PropertyDefinitionType[][];
};

interface EditorState {
  editorProperties: EditorProperty[];
  formFieldsData: PropertyDefinitionType[][];
  formFieldsDataById: LayoutsById;
  graphicData: GraphicData;
  metadata: Metadata;
  hasUnsavedChanges: boolean;
  isSubmittingDownloadGraphic: boolean;
  resolutions: Resolution[];
  resolutionsPreview: Resolution[];
  templateProperties: Record<string, string>;
  isLogoCustomizationPreview: boolean;
  tabValue: number;
  isReadyToRenderPreview: boolean;

  resetStore: () => void;
  setEditorProperties: (editorProperties: EditorProperty[]) => void;
  setFormFieldsData: (formFieldsData: PropertyDefinitionType[][]) => void;
  setFormFieldsDataById: (formFieldsData: LayoutsById) => void;
  setGraphicData: (graphicData: GraphicData) => void;
  setMetadata: (metaData: Metadata) => void;
  setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
  setIsSubmittingDownloadGraphic: (
    isSubmittingDownloadGraphic: boolean
  ) => void;
  setResolutions: (resolutions: Resolution[]) => void;
  setResolutionsPreview: (resolutions: Resolution[]) => void;
  setTemplateProperties: (templateProperties: Record<string, string>) => void;
  setIsLogoCustomizationPreview: (isLogoCustomizationPreview: boolean) => void;
  setTabValue: (tabValue: number) => void;
  setIsReadyToRenderPreview: (isReadyToRenderPreview: boolean) => void;
}

export const intialEditorStoreValues = {
  editorProperties: [],
  formFieldsData: undefined,
  formFieldsDataById: undefined,
  graphicData: {
    resources: null,
    properties: { values: null, definitions: null },
    hasEditorErrors: false
  },
  metadata: {
    template: {
      id: '',
      moduleId: ''
    },
    graphic: {
      id: '',
      name: ''
    }
  },
  hasUnsavedChanges: false,
  isSubmittingDownloadGraphic: false,
  resolutions: [],
  resolutionsPreview: [],
  templateProperties: null,
  isLogoCustomizationPreview: false,
  tabValue: 0,
  isReadyToRenderPreview: false
};

export const useEditorStore = create<EditorState>((set) => ({
  ...intialEditorStoreValues,
  setMetadata: (metadata) =>
    set(() => ({
      metadata
    })),
  setResolutions: (resolutions) =>
    set(() => ({
      resolutions
    })),
  setResolutionsPreview(resolutions) {
    set(() => ({
      resolutionsPreview: resolutions
    }));
  },
  setGraphicData: (graphicData) =>
    set(() => ({
      graphicData
    })),
  setFormFieldsData: (formFieldsData) =>
    set(() => ({
      formFieldsData
    })),
  setFormFieldsDataById: (formFieldsDataById) =>
    set(() => ({
      formFieldsDataById
    })),
  setIsSubmittingDownloadGraphic: (isSubmittingDownloadGraphic) =>
    set(() => ({
      isSubmittingDownloadGraphic
    })),
  setTemplateProperties: (templateProperties) =>
    set(() => ({ templateProperties })),
  setEditorProperties: (editorProperties) => set(() => ({ editorProperties })),
  setHasUnsavedChanges: (hasUnsavedChanges) =>
    set(() => ({ hasUnsavedChanges })),
  setIsLogoCustomizationPreview: (isLogoCustomizationPreview) =>
    set(() => ({ isLogoCustomizationPreview })),
  setTabValue: (tabValue) => set(() => ({ tabValue })),
  resetStore: () => set(() => ({ ...intialEditorStoreValues })),
  setIsReadyToRenderPreview: (isReadyToRenderPreview) =>
    set(() => ({ isReadyToRenderPreview }))
}));
